import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { fetchAppSettings } from '../../../features/settings/redux/settingsSlices';
import { fetchCurrentUserPermissions, userAdded, userRemoved } from '../../../features/user/redux/userSlices';
import {
    addMembersToGroup,
    createUserGroup,
    removeMemberFromGroup,
    removeUserGroup,
    setPermissionForGroupSuccess,
    setPermissionsForGroupSuccess
} from '../../../features/usergroup/redux/userGroupSlices';
import { AppSettingsDto, ResourceType } from '../../PricePrgClient';
import { guardedAction, hubListener, hubNotifier, signalROptions } from '../helpers';
import {
    notifyAppSettingsUpdated,
    notifyUserGroupMemberRemoved,
    notifyUserGroupMembersAdded,
    notifyUserGroupPermissionSet,
    notifyUserGroupPermissionsSet,
    notifyUserGroupRemoved,
    notifyUserGroupSaved
} from '../signalrActions';
import { SignalrHub } from '../signalrTypes';

export const userHub: SignalrHub = {
    hubName: 'user hub',
    url: '/signalr/userHub',
    autoReconnect: true,
    options: signalROptions
};

const userHub$ = hubListener(userHub, (hub, state$) => {
    function guardedUserAction<TPayload>(action: string, ac: ActionCreatorWithPayload<TPayload>) {
        return guardedAction(ResourceType.UserGroupPage, hub, state$, action, ac);
    }

    const userGroupSaved$ = guardedUserAction('UserGroupSaved', createUserGroup.success);
    const userGroupRemoved$ = guardedUserAction('UserGroupRemoved', removeUserGroup.success);
    const userGroupMembersAdded$ = guardedUserAction('UserGroupMembersAdded', addMembersToGroup.success);
    const userGroupMemberRemoved$ = guardedUserAction(
        'UserGroupMemberRemoved',
        removeMemberFromGroup.success
    );
    const userGroupPagePermissionSet$ = guardedUserAction(
        'UserGroupPermissionSet',
        setPermissionForGroupSuccess
    );
    const userGroupPagePermissionsSet$ = guardedUserAction(
        'UserGroupPermissionsSet',
        setPermissionsForGroupSuccess
    );
    const userAdded$ = guardedUserAction('UserAdded', userAdded);
    const userRemoved$ = guardedUserAction('UserRemoved', userRemoved);

    const userPermissionChanged$ = hub.on<ResourceType[] | null>('UserPermissionsChanged').pipe(
        withLatestFrom(state$.pipe(map(state => state.user.currentUserPermissions))),
        // No reason to refetch user permissions if user is admin. Admin got access to everything already
        filter(([, cup]) => !cup.isAdmin),
        map(([action]) => fetchCurrentUserPermissions.request(action))
    );

    const appSettingsUpdated$ = hub
        .on<AppSettingsDto>('AppSettingsUpdated')
        .pipe(map(fetchAppSettings.success));

    return [
        userGroupSaved$,
        userGroupRemoved$,
        userGroupMembersAdded$,
        userGroupMemberRemoved$,
        userGroupPagePermissionSet$,
        userGroupPagePermissionsSet$,
        userAdded$,
        userRemoved$,
        userPermissionChanged$,
        appSettingsUpdated$
    ];
});

const notifyUserGroupSaved$ = hubNotifier(userHub, notifyUserGroupSaved, (hub, payload) =>
    hub.send('NotifyUserGroupSaved', payload)
);

const notifyUserGroupMembersAdded$ = hubNotifier(userHub, notifyUserGroupMembersAdded, (hub, payload) =>
    hub.send('NotifyUserGroupMembersAdded', payload.userGroupId, payload.userIds)
);

const notifyUserGroupMemberRemoved$ = hubNotifier(userHub, notifyUserGroupMemberRemoved, (hub, payload) =>
    hub.send('NotifyUserGroupMemberRemoved', payload.userGroupId, payload.userId)
);

const notifyUserGroupPermissionSet$ = hubNotifier(userHub, notifyUserGroupPermissionSet, (hub, payload) =>
    hub.send('NotifyUserGroupPermissionSet', payload)
);

const notifyUserGroupPermissionsSet$ = hubNotifier(userHub, notifyUserGroupPermissionsSet, (hub, payload) =>
    hub.send('NotifyUserGroupPermissionsSet', payload)
);

const notifyUserGroupRemoved$ = hubNotifier(userHub, notifyUserGroupRemoved, (hub, payload) =>
    hub.send('NotifyUserGroupRemoved', payload)
);

const notifyAppSettingsUpdated$ = hubNotifier(userHub, notifyAppSettingsUpdated, hub =>
    hub.send('NotifyAppSettingsUpdated')
);

export default [
    userHub$,
    notifyUserGroupSaved$,
    notifyUserGroupMembersAdded$,
    notifyUserGroupMemberRemoved$,
    notifyUserGroupPermissionSet$,
    notifyUserGroupPermissionsSet$,
    notifyUserGroupRemoved$,
    notifyAppSettingsUpdated$
];
